import {
  getSPDataWithParams,
  postSPDataWithoutParams,
  putSPDataWithParams,
} from './services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

const EVENTS_BASE = '/api/secure/cc/support/events'
const ACTIONS_BASE = `${EVENTS_BASE}/actions`

function getResponseMessage(response, fallbackMessage) {
  return (
    response?.data?.message ||
    response?.response?.data?.message ||
    response?.message ||
    fallbackMessage
  )
}

function toActionResult(response, fallbackErrorMessage) {
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response),
      rawResponse: response,
    }
  }
  return {
    ok: false,
    message: getResponseMessage(response, fallbackErrorMessage),
    rawResponse: response,
  }
}

/** Marks the session completed on behalf of the practitioner. */
export async function markSupportBookedEventCompleted(bookedEventId) {
  const response = await putSPDataWithParams(`${EVENTS_BASE}/booked-event/finish`, null, {
    bookedEventId,
  })
  return toActionResult(response, 'Could not mark the session completed.')
}

/**
 * Reassigns the booking to another practitioner (role-aware per event family).
 * payload: { bookedEventId, newMentorId, sendCommunicationToClient, sendCommunicationToMentor, reason }
 */
export async function reassignSupportBookedEvent(payload) {
  const response = await postSPDataWithoutParams(`${ACTIONS_BASE}/reassign`, payload)
  return toActionResult(response, 'Could not reassign the booking.')
}

/**
 * Confirms an out-of-band payment on a payment-pending booking.
 * payload: { bookedEventId, reasonGiven, paymentTxnId, paymentGatewayUsed, feeCollectionNote }
 */
export async function markSupportBookedEventAsPaid(payload) {
  const response = await postSPDataWithoutParams(`${ACTIONS_BASE}/mark-as-paid`, {
    platform: 'web',
    ...payload,
  })
  return toActionResult(response, 'Could not mark the booking as paid.')
}

/** Toggles the test flag on a booking (test bookings are excluded from stats). */
export async function markSupportBookedEventAsTest(bookedEventId, markAsTest) {
  const path = markAsTest ? 'mark-as-test' : 'unmark-as-test'
  const response = await postSPDataWithoutParams(
    `${ACTIONS_BASE}/${path}?${new URLSearchParams({ bookedEventId })}`,
    null,
  )
  return toActionResult(
    response,
    markAsTest ? 'Could not mark the booking as test.' : 'Could not remove the test flag.',
  )
}

/** Overrides the video-call (RTC) provider for an online session. */
export async function updateSupportBookedEventRtcProvider({ bookedEventId, rtcProviderOverride }) {
  const response = await putSPDataWithParams(`${ACTIONS_BASE}/rtc-provider-override`, {
    bookedEventId,
    rtcProviderOverride: rtcProviderOverride || null,
  })
  return toActionResult(response, 'Could not update the video provider.')
}

/** Practitioner snippets for the reassign picker. Returns []. */
export async function searchSupportReassignPractitioners(search) {
  const response = await getSPDataWithParams(`${ACTIONS_BASE}/practitioners/search`, {
    search: search || undefined,
  })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return getDataMapFromCommonResponse(response)?.practitioners || []
  }
  return []
}

/** Sends the online-session join link over WhatsApp. kind: 'secure' | 'public'. */
export async function sendSupportBookedEventJoinLink({
  bookedEventId,
  kind = 'secure',
  toCustomMobile,
  toHumanSupport = false,
}) {
  const linkPath = kind === 'public' ? 'public-join-link' : 'secure-join-link'
  const params = new URLSearchParams({ 'booked-event-id': bookedEventId })
  let path
  if (toHumanSupport) {
    path = `${EVENTS_BASE}/${linkPath}/send-to-human-support?${params}`
  } else {
    if (toCustomMobile) {
      params.set('to-custom-mobile', toCustomMobile)
    }
    path = `${EVENTS_BASE}/${linkPath}/send?${params}`
  }
  const response = await postSPDataWithoutParams(path, null)
  return toActionResult(response, 'Could not send the join link.')
}

/** Raw video-session tokens (join links/codes) for the given booked events. Returns []. */
export async function getSupportVideoSessionTokens(bookedEventIds = []) {
  const response = await getSPDataWithParams(`${EVENTS_BASE}/video-session-tokens/get`, {
    'booked-event-ids': (bookedEventIds || []).filter(Boolean).join(','),
  })
  if (response?.status === 200 && Array.isArray(response?.data)) {
    return response.data
  }
  return []
}

/**
 * A page of booked-event logs (session timeline + the support action audit trail).
 * Returns { logs, totalElements }.
 */
export async function getSupportBookedEventLogs(bookedEventId, { page = 0, size = 200 } = {}) {
  const response = await getSPDataWithParams(`${EVENTS_BASE}/logs/get`, {
    'booked-event-id': bookedEventId,
    page,
    size,
    sort: 'createdTime,desc',
  })
  if (response?.status === 200 && Array.isArray(response?.data?.content)) {
    return {
      logs: response.data.content,
      totalElements: response.data.totalElements || response.data.content.length,
    }
  }
  return { logs: [], totalElements: 0 }
}
